import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/Button'
import { BottomSheet } from '@/components/BottomSheet'
import { DateScrubber } from '@/components/DateScrubber'
import { MonthCalendar } from '@/components/MonthCalendar'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { supabase } from '@/lib/supabase'
import type { EventRow, JSONValue } from '@/lib/types'
import { absoluteTime, relativeTime, volumeString } from '@/lib/format'
import { PACK_COLORS, packForEvent } from '@/design-system/tokens'
import { IconForPack, NoteIcon, TimelineIcon } from '@/design-system/icons'
import { editRouteFor } from '@/lib/editEvent'
import { signedAttachmentUrl } from '@/lib/attachments'

const PAGE_SIZE = 50

export function TimelineScreen() {
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const nav = useNavigate()
  const userId = useAppStore((s) => s.user?.id)
  const [params] = useSearchParams()
  const routeParams = useParams<{ id: string }>()
  const sourceId = routeParams.id ?? params.get('event')
  const [filterDate, setFilterDate] = useState<string | null>(null)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const now = new Date()
  const [calendarMonth, setCalendarMonth] = useState<{ year: number; month: number }>(
    { year: now.getFullYear(), month: now.getMonth() },
  )
  // Exact source lookup is independent of pagination/focused-person filters. RLS
  // plus the active family scope still applies; a link is never read permission.
  const sourceQ = useQuery({
    enabled: !!familyId && !!userId && !!sourceId,
    queryKey: ['timeline-source', userId, familyId, sourceId],
    staleTime: 0, gcTime: 0, retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.from('events').select('*')
        .eq('family_id', familyId!).eq('id', sourceId!).maybeSingle()
      if (error) throw error
      return data as EventRow | null
    },
  })

  const query = useInfiniteQuery({
    enabled: !!familyId,
    queryKey: ['timeline', familyId, person?.id ?? 'family'],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      // Cursor is the last-seen `occurred_at` — page next by fetching everything older.
      let q = supabase
        .from('events')
        .select('*')
        .eq('family_id', familyId!)
        .is('supersedes_event_id', null)
        .order('occurred_at', { ascending: false })
        .order('server_sequence', { ascending: false })
        .limit(PAGE_SIZE)
      if (person) q = q.eq('person_id', person.id)
      if (pageParam) q = q.lt('occurred_at', pageParam)
      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as EventRow[]
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined
      const last = lastPage[lastPage.length - 1]
      return last?.occurred_at ?? undefined
    },
  })

  const allEvents = useMemo(() => (query.data?.pages ?? []).flat(), [query.data])

  // Per-day activity density for the currently visible calendar month. Cheap query — only
  // fetches occurred_at (no payload), grouped client-side. Powers the calendar heat tint
  // AND the DateScrubber dots for days that aren't in the currently loaded pagination pages.
  const densityQ = useQuery({
    enabled: !!familyId,
    queryKey: ['timeline-density', familyId, person?.id ?? 'family', calendarMonth.year, calendarMonth.month],
    queryFn: async () => {
      // Extend the range to also cover the last 30 days for the scrubber, even if the
      // calendar month is further back. This is a small over-fetch that keeps the scrubber
      // dots live regardless of which month the calendar sheet was last on.
      const monthStart = new Date(calendarMonth.year, calendarMonth.month, 1)
      const monthEnd = new Date(calendarMonth.year, calendarMonth.month + 1, 1)
      const scrubberStart = new Date(); scrubberStart.setDate(scrubberStart.getDate() - 30); scrubberStart.setHours(0, 0, 0, 0)
      const from = new Date(Math.min(monthStart.getTime(), scrubberStart.getTime())).toISOString()
      const to = new Date(Math.max(monthEnd.getTime(), Date.now())).toISOString()
      let q = supabase.from('events').select('occurred_at')
        .eq('family_id', familyId!)
        .is('supersedes_event_id', null)
        .gte('occurred_at', from).lt('occurred_at', to)
        .limit(5000)
      if (person) q = q.eq('person_id', person.id)
      const { data, error } = await q
      if (error) throw error
      const map = new Map<string, number>()
      for (const row of (data ?? []) as { occurred_at: string }[]) {
        const d = new Date(row.occurred_at)
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        map.set(iso, (map.get(iso) ?? 0) + 1)
      }
      return map
    },
  })
  const activityByDay = densityQ.data ?? new Map<string, number>()

  // Fetch attachments for the visible events so Timeline can inline photo thumbnails.
  const eventIds = useMemo(() => allEvents.map((e) => e.id), [allEvents])
  const attachmentsQ = useQuery({
    enabled: eventIds.length > 0,
    queryKey: ['timeline_attachments', familyId, eventIds.slice(0, 20).join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('attachments')
        .select('id, event_id, media_type, storage_ref, mime_type')
        .in('event_id', eventIds)
      if (error) throw error
      const byEvent = new Map<string, Array<{ id: string; storage_ref: string; media_type: string }>>()
      for (const a of data ?? []) {
        if (!a.event_id || !a.storage_ref) continue
        const arr = byEvent.get(a.event_id as string) ?? []
        arr.push({ id: a.id as string, storage_ref: a.storage_ref as string, media_type: a.media_type as string })
        byEvent.set(a.event_id as string, arr)
      }
      return byEvent
    },
  })

  const byDay = useMemo(() => {
    // Filter out superseded events client-side too (belt-and-suspenders on the query filter).
    const supersededIds = new Set(allEvents.map((r) => r.supersedes_event_id).filter((x): x is string => !!x))
    let visible = allEvents.filter((r) => !supersededIds.has(r.id))
    if (filterDate) {
      // Filter to events whose local-time date equals the selected ISO date.
      visible = visible.filter((r) => {
        const d = new Date(r.occurred_at)
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        return iso === filterDate
      })
    }
    const map = new Map<string, EventRow[]>()
    for (const r of visible) {
      const key = new Date(r.occurred_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })
      const bucket = map.get(key) ?? []
      bucket.push(r)
      map.set(key, bucket)
    }
    return Array.from(map.entries())
  }, [allEvents, filterDate])

  const filteredDateLabel = filterDate
    ? new Date(filterDate + 'T12:00:00').toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
    : null

  return (
    <Screen
      title={person ? `${person.display_name}'s timeline` : 'Timeline'}
      action={
        <button
          type="button"
          onClick={() => setCalendarOpen(true)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
          aria-label="Open month calendar"
        >
          <CalendarGlyph />
        </button>
      }
    >
      <DateScrubber
        selectedDate={filterDate}
        onSelect={setFilterDate}
        activityByDay={activityByDay}
      />
      <BottomSheet
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        title={person ? `${person.display_name}'s months` : 'Months'}
      >
        <MonthCalendar
          year={calendarMonth.year}
          month={calendarMonth.month}
          activityByDay={activityByDay}
          selectedDate={filterDate}
          onSelect={(iso) => { setFilterDate(iso); setCalendarOpen(false) }}
          onChangeMonth={(delta) => setCalendarMonth((c) => {
            const d = new Date(c.year, c.month + delta, 1)
            return { year: d.getFullYear(), month: d.getMonth() }
          })}
        />
      </BottomSheet>
      <div className="px-4 py-3">
        {filteredDateLabel && (
          <div className="mb-4 flex items-center justify-between rounded-r-sm bg-bg-elevated border border-divider-subtle px-3 py-2">
            <div className="text-caption">
              <span className="text-text-tertiary">Showing </span>
              <span className="text-text-primary font-medium">{filteredDateLabel}</span>
              {byDay.length === 0 && !query.isLoading && (
                <span className="text-text-tertiary"> — nothing recorded that day</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setFilterDate(null)}
              className="text-caption text-text-tertiary underline underline-offset-2 hover:text-text-primary"
            >
              Show all
            </button>
          </div>
        )}
        {sourceId && (
          <section aria-label="Cited source event" className="mb-6 space-y-3">
            <Kicker>Cited source event</Kicker>
            <p className="text-caption text-text-secondary break-all">{sourceId}</p>
            {sourceQ.isFetching ? <p role="status">Checking source access…</p>
              : sourceQ.isError || !sourceQ.data ? <p>Source unavailable or access changed.</p>
              : <>
                <EventRowItem event={sourceQ.data} attachments={[]} onEdit={() => {
                  const route = editRouteFor(sourceQ.data!)
                  if (route) nav(route)
                }} />
                <p className="text-caption text-text-secondary">Exact record cited at response time; later corrections may change its interpretation.</p>
              </>}
          </section>
        )}
        {query.isLoading && <div className="py-8 text-center text-text-tertiary text-caption">Loading…</div>}
        {query.isError && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {(query.error as Error).message}
          </div>
        )}
        {allEvents.length === 0 && !query.isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="rounded-r-sm border border-divider-subtle bg-bg-elevated py-12 px-6 text-center"
          >
            <TimelineIcon size={32} className="mx-auto text-text-quiet" />
            <div className="mt-4 font-serif italic text-title text-text-secondary">Nothing recorded yet.</div>
            <div className="mt-2 text-caption text-text-tertiary max-w-xs mx-auto">
              Every feed, diaper, sleep, medication and note lands here — grouped by day, tap any event to correct it.
            </div>
          </motion.div>
        )}
        <div className="space-y-6">
          {byDay.map(([day, rows]) => (
            <section key={day}>
              <Kicker>{day}</Kicker>
              <div className="mt-2 relative pl-4">
                <div className="absolute left-1 top-2 bottom-2 w-px bg-divider-subtle" />
                <div className="space-y-3">
                  {rows.map((r, i) => (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18, delay: Math.min(i * 0.025, 0.4) }}
                    >
                      <EventRowItem
                        event={r}
                        attachments={attachmentsQ.data?.get(r.id) ?? []}
                        onEdit={() => {
                          const route = editRouteFor(r)
                          if (route) nav(route)
                        }}
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          ))}
        </div>

        {allEvents.length > 0 && query.hasNextPage && (
          <div className="pt-4 pb-6 flex justify-center">
            <Button
              variant="secondary"
              size="md"
              loading={query.isFetchingNextPage}
              onClick={() => query.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        )}
        {allEvents.length > 0 && !query.hasNextPage && (
          <div className="pt-4 pb-6 text-center text-caption text-text-quiet">
            End of history — {allEvents.length} event{allEvents.length === 1 ? '' : 's'}.
          </div>
        )}
      </div>
    </Screen>
  )
}

function EventRowItem({
  event,
  attachments,
  onEdit,
}: {
  event: EventRow
  attachments: Array<{ id: string; storage_ref: string; media_type: string }>
  onEdit: () => void
}) {
  const pack = packForEvent(event.event_type)
  const color = PACK_COLORS[pack].light
  const Icon = event.event_type === 'note.created' ? NoteIcon : IconForPack[pack]
  const summary = eventSummary(event)
  const editable = editRouteFor(event) != null
  const isCorrection = event.event_type.endsWith('.corrected')

  const imageAttachments = attachments.filter((a) => a.media_type === 'image')

  return (
    <div className="relative group">
      <div
        className="absolute -left-3 top-2 h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 8px ${color}` }}
      />
      <div
        className="rounded-r-sm bg-bg-elevated border px-3 py-2.5"
        style={{ borderColor: isCorrection ? color + '33' : 'rgba(244,239,231,0.07)' }}
      >
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full flex items-center justify-center" style={{ background: color + '22', color }}>
            <Icon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-body text-text-primary truncate flex items-center gap-1.5">
              <span className="truncate">{summary.title.replace(' (edited)', '')}</span>
              {isCorrection && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-tag font-medium shrink-0"
                  style={{ background: color + '20', color }}
                  title={`Edited ${relativeTime(event.recorded_at ?? event.occurred_at)}`}
                >
                  <EditGlyph size={9} />
                  edited
                </span>
              )}
            </div>
            {summary.subtitle && <div className="text-caption text-text-tertiary truncate">{summary.subtitle}</div>}
          </div>
          <div className="text-caption text-text-quiet whitespace-nowrap">{absoluteTime(event.occurred_at)}</div>
        </div>

        {imageAttachments.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {imageAttachments.map((a) => (
              <AttachmentThumb key={a.id} storageRef={a.storage_ref} />
            ))}
          </div>
        )}

        {editable && (
          <div className="pt-2 mt-2 border-t border-divider-subtle flex justify-end">
            <button
              type="button"
              onClick={onEdit}
              className="text-caption text-text-tertiary underline underline-offset-2 hover:text-text-secondary"
            >
              Edit
            </button>
          </div>
        )}
      </div>
      <div className="text-tag text-text-quiet mt-1 pl-2">{relativeTime(event.occurred_at)}</div>
    </div>
  )
}

function CalendarGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 10h18M8 2v4M16 2v4" />
      <circle cx="8" cy="15" r="0.6" fill="currentColor" />
      <circle cx="12" cy="15" r="0.6" fill="currentColor" />
      <circle cx="16" cy="15" r="0.6" fill="currentColor" />
    </svg>
  )
}

function EditGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  )
}

function AttachmentThumb({ storageRef }: { storageRef: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const signed = await signedAttachmentUrl(storageRef, 300)
      if (!cancelled) setUrl(signed)
    })()
    return () => { cancelled = true }
  }, [storageRef])
  if (!url) {
    return <div className="h-20 w-20 rounded-r-xs bg-bg-raised border border-divider-subtle shrink-0" />
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block shrink-0">
      <img src={url} alt="attachment" className="h-20 w-20 rounded-r-xs object-cover border border-divider-subtle" />
    </a>
  )
}

function eventSummary(e: EventRow): { title: string; subtitle: string | null } {
  const p = e.payload as Record<string, JSONValue>
  switch (e.event_type) {
    case 'early_care.feeding.recorded':
    case 'early_care.feeding.corrected': {
      const ft = p['feed_type'] as string | undefined
      const vol = p['volume_ml'] as number | undefined
      const dur = p['duration_minutes'] as number | undefined
      const label = prettyFeed(ft) + (e.event_type.endsWith('.corrected') ? ' (edited)' : '')
      return { title: label, subtitle: vol != null ? volumeString(vol) : dur != null ? `${dur} min` : null }
    }
    case 'early_care.diaper.recorded':
    case 'early_care.diaper.corrected': {
      const dt = p['diaper_type'] as string | undefined
      const suffix = e.event_type.endsWith('.corrected') ? ' (edited)' : ''
      return { title: `Diaper · ${dt ?? '—'}${suffix}`, subtitle: (p['color'] as string | undefined) ?? null }
    }
    case 'early_care.sleep.started':
      return { title: 'Sleep started', subtitle: (p['location'] as string | undefined) ?? null }
    case 'early_care.sleep.ended':
      return { title: 'Sleep ended', subtitle: null }
    case 'early_care.medication.recorded':
    case 'early_care.medication.corrected': {
      const name = p['medication_name'] as string | undefined
      const amt = p['amount'] as number | undefined
      const unit = p['amount_unit'] as string | undefined
      const suffix = e.event_type.endsWith('.corrected') ? ' (edited)' : ''
      return { title: `Medication · ${name ?? '—'}${suffix}`, subtitle: amt != null ? `${amt} ${unit ?? ''}`.trim() : null }
    }
    case 'growth.recorded': {
      const mt = (p['measurement_type'] as string | undefined) ?? 'measurement'
      const v = p['value'] as number | undefined
      const u = p['unit'] as string | undefined
      return { title: `Growth · ${mt.replace(/_/g, ' ')}`, subtitle: v != null ? `${v} ${u ?? ''}`.trim() : null }
    }
    case 'note.created':
      return { title: 'Note', subtitle: ((p['text'] as string | undefined) ?? '').slice(0, 80) || null }
    case 'family.handoff.created':
      return { title: 'Handoff', subtitle: (p['note'] as string | undefined) ?? null }
    case 'person.created':
      return { title: `Added ${(p['display_name'] as string) ?? 'a person'}`, subtitle: null }
    case 'person.updated': {
      const t = p['transition'] as string | undefined
      if (t === 'unborn_to_baby') return { title: `Born! ${(p['display_name'] as string) ?? ''}`.trim(), subtitle: null }
      return { title: 'Person updated', subtitle: null }
    }
    case 'family.created':
      return { title: 'Family created', subtitle: null }
    default:
      return { title: e.event_type, subtitle: null }
  }
}

function prettyFeed(k: string | undefined): string {
  switch (k) {
    case 'bottle_formula': return 'Bottle · formula'
    case 'bottle_breastmilk': return 'Bottle · breast milk'
    case 'breast_left': return 'Breast · left'
    case 'breast_right': return 'Breast · right'
    case 'both': return 'Both breasts'
    case 'solid': return 'Solid food'
    default: return 'Feed'
  }
}
